import { useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, makeStyles, ButtonBase } from '@material-ui/core';
import moment from 'moment';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import BusStop from '../components/BusStop';
import { DropDownIcon, DropUpIcon } from '../../../components/Icon';
import { eadmissionAPI } from '../../../apis';
import { getUserInfo } from '../../../utils';
import useUpdateEffect from '../../../utils/useUpdateEffect';
import useIpad from '../../../utils/useIpad';
import Text from '../../../components/Text';
import AutoCompleteInput from '../../../components/AutoCompleteInput';
import { AdmissionContext } from '..';
import { GENDER, WT } from '../../../comm/constant';
import AutoCompleteMultiCheckbox from '../../../components/AutoCompleteMultiCheckbox';
import { BrowserAlertByFilter } from '../Actionable/ActionableHeader';
import AeNoScanInput from '../components/AeNoScanInput';
import { handleAdmissionFormRedirect } from '../../../utils/admissionUtil';
import StatusBar from './StatusBar';

const useStyles = makeStyles(() => ({
  detailBtn: ({ detailOpen }) => ({
    fontFamily: 'NotoSans-Medium',
    color: detailOpen ? '#7d86a9' : '#fff',
    fontSize: '0.9rem',
    width: '11.25rem',
    minWidth: '11.25rem',
    height: '1.7rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '4px',
    backgroundColor: detailOpen ? '#fff' : '#7d86a9',
  }),
  dropIcon: {
    fontSize: '0.875rem',
    marginLeft: '1rem',
  },
  reset: {
    color: '#538FFF',
    fontSize: '1rem',
    textDecoration: 'underline',
    fontFamily: 'NotoSans-Bold',
  },
}));

const initData = () => ({
  admRdyTime: null,
  admissions: [],
  lastUpdatedTime: null,
  admDecTime: null,
  holdCount: null,
  pendingCount: null,
  withdrawnCount: null,
  completedCount: null,
  progressCount: null,
  admStatic: {
    maleCount: null,
    femaleCount: null,
    priority1: null,
    // priority2: null,
    // priority3: null,
    // priority4: null,
    priorityNa: null,
  },
  admSpecStatic: [],
});

const initAdmissions = (admissions, showBusStop) => {
  return _.flatMap(admissions, (obj) => [
    { ...obj, busStopRow: false, display: true, otherInfo: false },
    { ...obj, busStopRow: false, display: true, otherInfo: true },
    { ...obj, busStopRow: true, display: showBusStop, otherInfo: false },
  ]);
};

const pageSize = 30;
const EmergencyAdmission = ({ contentHeight }) => {
  const {
    tabValue,
    tabValues,
    showBusStop,
    forMyAction,
    filter,
    admissionParamsRef,
    specialtyList,
    setFilter,
    handleFilterSelect,
    initFilter,
    needRefresh,
    busstopList,
  } = useContext(AdmissionContext);
  const [detailOpen, setDetailOpen] = useState(false);
  const classes = useStyles({ detailOpen });
  const [data, setData] = useState(initData());
  const [pageNo, setPageNo] = useState(1);
  const [maxCount, setMaxCount] = useState(0);
  // 防止滚动到底部时在同一次加载过程中多次触发分页请求
  const [isLoading, setIsLoading] = useState(false);
  const [sort, setSort] = useState({
    sortKey: '',
    sortOrder: '',
  });
  const userInfo = getUserInfo();
  const fullScreen = useSelector((state) => state.fullScreenReducer);
  const [actionWardId, setActionWardId] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const rouList = useSelector((state) => state.rouList);
  const history = useHistory();
  const isIpad = useIpad();
  const pendingBarcodeAeNoRef = useRef('');
  const lastAdmissionRequestKeyRef = useRef('');
  const tabContentHeight = contentHeight > 0 ? `${contentHeight}px` : `calc(100vh - ${fullScreen ? 5 : 10}rem)`;

  const handleResetFilter = () => {
    pendingBarcodeAeNoRef.current = '';
    setFilter(initFilter());
  };

  const handleAeNoBarcodeScan = useCallback(
    (scanValue) => {
      if (!scanValue || isLoading) {
        return;
      }
      pendingBarcodeAeNoRef.current = scanValue;
    },
    [isLoading]
  );

  useEffect(() => {
    setPageNo(1);
  }, [filter]);

  // 使用自定义的 useUpdateEffect Hook
  useUpdateEffect(() => {
    setData(initData());
    setRefresh((o) => !o);
    setPageNo(1);

    if (fullScreen) {
      const interval = setInterval(() => {
        setData(initData());
        setRefresh((o) => !o);
        setPageNo(1);
      }, 1000 * 60 * 5);

      return () => clearInterval(interval);
    }
  }, [fullScreen]); // 只在 fullScreen 变化时触发 effect

  useEffect(() => {
    setPageNo(1);
    setData(initData());
    setMaxCount(0);
  }, [tabValue]);

  useEffect(() => {
    setActionWardId(forMyAction ? userInfo.wardId : 0);
    setPageNo(1);
  }, [forMyAction]);

  const handleOpenDetail = () => {
    setDetailOpen((o) => !o);
  };

  const admissionParams = useMemo(() => {
    const { startDate, endDate, waitTime, specialtys, busstop_C, busstop_PD, ...otherFilter } = filter;
    const params = {
      // pending 直接load所有数据，不分页
      pageSize: tabValue === tabValues.Completed ? pageSize : null,
      pageNo: tabValue === tabValues.Completed ? pageNo : null,
      type: tabValue,
      sort: {
        column: sort.sortKey,
        order: sort.sortOrder,
      },
      filter: {
        actionWardId,
        ...otherFilter,
        handingByIds: _.map(otherFilter?.handingByIds, (item) => item.value),
        startDate: tabValue === tabValues.Completed && startDate ? moment(startDate).format('YYYY-MM-DD') : null,
        endDate: tabValue === tabValues.Completed && endDate ? moment(endDate).format('YYYY-MM-DD') : null,
        waitTime: waitTime === '' ? '0' : waitTime,
        specialtys: _.map(specialtys, (item) => item.value),
        busstops: _.map(tabValue === tabValues.Completed ? busstop_C : busstop_PD, (item) => item.value),
      },
    };
    return params;
  }, [filter, tabValue, sort, pageSize, pageNo, actionWardId]);

  const admissionRequestKey = useMemo(() => {
    return JSON.stringify({
      admissionParams,
      refresh,
      needRefresh,
    });
  }, [admissionParams, refresh, needRefresh]);

  useImperativeHandle(admissionParamsRef, () => ({
    admissionParams,
    setRefresh,
  }));

  const getAdmissionCount = async (params) => {
    try {
      const { result } = await eadmissionAPI.getAdmissionCount(params);
      setData((o) => ({
        ...o,
        ...result,
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const getAdmissionList = async (params) => {
    let barcodeRedirectList = [];

    try {
      setIsLoading(true);
      if (params.type === tabValues.Pending) {
        getAdmissionCount(params);
      }

      const { result } = await eadmissionAPI.getAdmissionList(params);
      const resultData = result?.data ?? [];
      barcodeRedirectList = resultData;

      setMaxCount(result.count);

      if (pageNo === 1) {
        setData((o) => ({
          ...o,
          admissions: initAdmissions(resultData, showBusStop.show),
        }));
      } else {
        // 因为 initAdmissions 会把每个 admission 扩展成 3 行，所以每页实际行数是 pageSize * 3
        setData((preData) => {
          return {
            ...preData,
            admissions: [
              ...preData.admissions.slice(0, (pageNo - 1) * pageSize * 3),
              ...initAdmissions(resultData, showBusStop.show),
            ],
          };
        });
      }
    } catch (error) {
      handleAdmissionFormRedirect({
        forceClear: true,
        params,
        pendingBarcodeAeNoRef,
      });
    } finally {
      setIsLoading(false);
    }

    handleAdmissionFormRedirect({
      admissionList: barcodeRedirectList,
      params,
      pendingBarcodeAeNoRef,
      redirectCallBack: (eaId = '') => {
        history.push(`/admissionForm/${eaId}`);
      },
      isIpad,
      isLogin: userInfo?.flag,
    });
  };

  useEffect(() => {
    if (lastAdmissionRequestKeyRef.current === admissionRequestKey) {
      return;
    }
    lastAdmissionRequestKeyRef.current = admissionRequestKey;
    getAdmissionList(admissionParams);
  }, [admissionParams, admissionRequestKey, rouList]);

  const handleScroll = useCallback(
    ({ scrollTop, clientHeight, scrollHeight }) => {
      // pending 直接load所有数据，不分页
      if (tabValue === tabValues.Pending) {
        return;
      }
      // 只有在未处于加载中且尚未加载完所有数据时才触发下一页
      if (!isLoading && scrollTop + clientHeight + 1 >= scrollHeight) {
        if (data.admissions.length < maxCount * 2) {
          setPageNo((value) => value + 1);
        }
      }
    },
    [maxCount, data, tabValue, isLoading]
  );

  return (
    <Box height={tabContentHeight} maxHeight={tabContentHeight} p="0.875rem 1.87rem 0">
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        p="1rem"
        borderRadius="0.9375rem"
        bgcolor="#FFF"
        height="100%"
        overflow="auto hidden"
      >
        <Box height="100%" minWidth={isIpad ? 'auto' : '100rem'} display="flex" flexDirection="column">
          {/* Filter */}
          <Box display="flex" flexWrap="wrap" justifyContent="space-between" mb="0.7rem" gridGap="0.75rem">
            <Box display="flex" flexWrap="wrap" gridGap="0.75rem" alignItems="center">
              <Text color="#3E3E3E" fontSize="1rem">
                Filter:
              </Text>
              <AutoCompleteMultiCheckbox
                itemList={specialtyList}
                value={filter.specialtys}
                onChange={handleFilterSelect('specialtys')}
                width="8rem"
                height="2.5625rem"
                placeholder="Specialty"
                freeSolo
                highlight="#FFFFCC"
                selectAll={false}
              />
              <AutoCompleteInput
                itemList={initItemList(GENDER)}
                value={filter.sex}
                onChange={handleFilterSelect('sex')}
                width="6rem"
                height="2.5625rem"
                placeholder="Gender"
                highlight="#FFFFCC"
              />
              <AutoCompleteInput
                itemList={WT}
                value={filter.waitTime}
                onChange={handleFilterSelect('waitTime')}
                width="6rem"
                height="2.5625rem"
                placeholder="W.T."
                highlight="#FFFFCC"
              />
              <AutoCompleteMultiCheckbox
                itemList={busstopList}
                value={filter[`busstop_${tabValue}`]}
                onChange={handleFilterSelect(`busstop_${tabValue}`)}
                width="15rem"
                height="2.5625rem"
                placeholder="Bus Stop"
                freeSolo
                highlight="#FFFFCC"
                selectAll={false}
              />
              <AeNoScanInput
                value={filter?.aeNo}
                onChange={handleFilterSelect('aeNo')}
                onBarcodeScan={handleAeNoBarcodeScan}
                disabled={isLoading}
                width="9rem"
              />
              <ButtonBase className={classes.reset} onClick={handleResetFilter}>
                Reset
              </ButtonBase>
              <BrowserAlertByFilter />
            </Box>
            <Box display="flex" alignItems="center">
              <Text fontSize="1rem">
                Last updated time: {data?.lastUpdatedTime && moment(data?.lastUpdatedTime).format('YYYY-MM-DD HH:mm')}
              </Text>
            </Box>
          </Box>

          {tabValue === tabValues.Pending && (
            <>
              {detailOpen && <StatusBar data={data} />}
              {/* detail bar */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb="0.7rem">
                <ButtonBase className={classes.detailBtn} onClick={handleOpenDetail}>
                  Statistic Detail
                  {detailOpen ? (
                    <DropUpIcon className={classes.dropIcon} />
                  ) : (
                    <DropDownIcon className={classes.dropIcon} />
                  )}
                </ButtonBase>
                <Box width="100%" height="1px" bgcolor="#ddd" />
              </Box>
            </>
          )}

          <BusStop
            data={data.admissions}
            forMyAction={forMyAction}
            handleScroll={handleScroll}
            detailOpen={detailOpen}
            completed={tabValue === tabValues.Completed}
            sort={sort}
            setSort={setSort}
            setPageNo={setPageNo}
            tabValue={tabValue}
          />
        </Box>
      </Box>
    </Box>
  );
};

const initItemList = (itemList) => {
  return [{ text: '', value: '' }, ...itemList];
};

export default EmergencyAdmission;
